import {isNil, isNotNil} from 'ramda';

import {getSafely} from 'shared/web';

import {PAGE_SIZE as ADS_PAGE_SIZE} from '../../../pages/AdvertisementPage/consts/pageSize';
import {GetStatisticsV2ApiResponse, StatisticsCar} from '../../../types/api/api';
import {PriceReportType} from '../../../types/PriceReportType';
import {isSoldIn30Days} from '../../../utils/isSoldIn30Days';

/** How many advertisement listing pages the price ladder shows (1st, 2nd, 3rd page end). */
export const PRICE_LADDER_PAGE_COUNT = 3;

/** Upper bound of points kept for the preview sparkline curve. */
const PRICE_CURVE_MAX_POINTS = 48;

/** Evenly samples the sorted prices down to at most PRICE_CURVE_MAX_POINTS points. */
function downsamplePriceCurve(sortedPrices: number[]): number[] {
  if (sortedPrices.length <= PRICE_CURVE_MAX_POINTS) {
    return sortedPrices;
  }
  return Array.from({length: PRICE_CURVE_MAX_POINTS}, (_, point) => {
    const index = Math.round((point / (PRICE_CURVE_MAX_POINTS - 1)) * (sortedPrices.length - 1));
    return getSafely(sortedPrices, index) ?? 0;
  });
}

export type MarketMetricsGroup = {
  count: number;
  averagePrice: number | null;
};

export type MarketMetricsPosition = {
  /** 1-based rank of my price among advertised cars sorted by price ascending. */
  place: number;
  /** Number of advertised cars the rank is relative to. */
  total: number;
  /** place / total in percent, rounded. */
  percent: number;
};

export type MarketMetricsSupplyDemand = {
  /** sold count minus advertised count (negative = supply exceeds demand). */
  countDelta: number;
  /** average sold price minus average advertised price. */
  averagePriceDelta: number | null;
  /** Ratio of the larger side over the smaller one (≥ 1); null when either side is 0. */
  ratio: number | null;
  demandExceedsSupply: boolean;
};

export type MarketMetrics = {
  totalHits: number;
  /** True when the response page does not contain all cars — counts are then lower bounds. */
  isTruncated: boolean;
  advertised: MarketMetricsGroup & {
    minPrice: number | null;
    maxPrice: number | null;
  };
  /** All cars sold within the backend 90-day window. */
  sold: MarketMetricsGroup;
  /** Subset of sold: sold within the last 30 days. */
  sold30Count: number;
  position: MarketMetricsPosition | null;
  /**
   * Price of the last (most expensive) advertised car on each ads listing page,
   * index 0 = 1st page. Null when the page does not exist.
   */
  pageEndPrices: (number | null)[];
  /**
   * Advertised prices sorted ascending, downsampled — the ascending curve of the
   * preview sparkline (my price marks its rank on it).
   */
  priceCurve: number[];
  supplyDemand: MarketMetricsSupplyDemand;
};

/**
 * Cross-system convention: first place on a single-vehicle market (1/1) shows 10 %
 * instead of 100 %, so “I am at the very start” stays visually obvious (100 % also
 * misbehaves in downstream filtering).
 */
function positionPercent(place: number, total: number): number {
  if (place === 1 && total === 1) {
    return 10;
  }
  return total > 0 ? Math.round((place / total) * 100) : 0;
}

function averagePrice(cars: StatisticsCar[]): number | null {
  const prices = cars.map((car) => car.price_with_vat).filter(isNotNil);
  if (prices.length === 0) {
    return null;
  }
  return Math.round(prices.reduce((sum, price) => sum + price, 0) / prices.length);
}

function computePageEndPrices(sortedAdvertisedPrices: number[]): (number | null)[] {
  return Array.from({length: PRICE_LADDER_PAGE_COUNT}, (_, pageIndex) => {
    const pageExists = sortedAdvertisedPrices.length > pageIndex * ADS_PAGE_SIZE;
    if (!pageExists) {
      return null;
    }
    const lastIndexOnPage =
      Math.min((pageIndex + 1) * ADS_PAGE_SIZE, sortedAdvertisedPrices.length) - 1;
    return sortedAdvertisedPrices[lastIndexOnPage] ?? null;
  });
}

function computePosition(
  sortedAdvertisedPrices: number[],
  myPrice: number | null
): MarketMetricsPosition | null {
  if (isNil(myPrice) || sortedAdvertisedPrices.length === 0) {
    return null;
  }
  const cheaperCount = sortedAdvertisedPrices.filter((price) => price < myPrice).length;
  const place = cheaperCount + 1;
  const total = sortedAdvertisedPrices.length;
  return {place, total, percent: positionPercent(place, total)};
}

function computeSupplyDemand(
  advertised: MarketMetricsGroup,
  sold: MarketMetricsGroup
): MarketMetricsSupplyDemand {
  const demandExceedsSupply = sold.count > advertised.count;
  const hasBothSides = sold.count > 0 && advertised.count > 0;
  const ratio = hasBothSides
    ? Math.max(sold.count, advertised.count) / Math.min(sold.count, advertised.count)
    : null;
  const averagePriceDelta =
    isNotNil(sold.averagePrice) && isNotNil(advertised.averagePrice)
      ? sold.averagePrice - advertised.averagePrice
      : null;

  return {
    countDelta: sold.count - advertised.count,
    averagePriceDelta,
    ratio,
    demandExceedsSupply,
  };
}

export type ExactTotals = {
  advertisedCount?: number | null;
  soldCount?: number | null;
  advertisedMaxPrice?: number | null;
};

/**
 * Fixes up metrics computed from a truncated response (API caps at 1000 cars — the
 * cheapest ones) with exact totals fetched by follow-up probe queries. Min and
 * page-end prices come from the cheapest cars and are always exact; counts, max
 * price, position total and the supply×demand figures are recomputed here.
 * Averages remain approximations over the returned subset.
 */
export function applyExactTotals(metrics: MarketMetrics, totals: ExactTotals): MarketMetrics {
  const advertisedCount = totals.advertisedCount ?? metrics.advertised.count;
  const soldCount = totals.soldCount ?? metrics.sold.count;
  const returnedAdvertisedCount = metrics.advertised.count;

  // My rank is exact only when my price fits within the returned (cheapest) cars;
  // above them the true place is unknown.
  let position = metrics.position;
  if (isNotNil(position)) {
    position =
      position.place <= returnedAdvertisedCount
        ? {
            place: position.place,
            total: advertisedCount,
            percent: positionPercent(position.place, advertisedCount),
          }
        : null;
  }

  const advertisedGroup: MarketMetricsGroup = {
    count: advertisedCount,
    averagePrice: metrics.advertised.averagePrice,
  };
  const soldGroup: MarketMetricsGroup = {count: soldCount, averagePrice: metrics.sold.averagePrice};

  return {
    ...metrics,
    advertised: {
      ...metrics.advertised,
      count: advertisedCount,
      // A truncated page holds only the cheapest cars, so its own max is meaningless —
      // the max is either the probe result or unknown (probe pending, or the market
      // exceeds the backend pagination cap).
      maxPrice: totals.advertisedMaxPrice ?? null,
    },
    sold: soldGroup,
    position,
    supplyDemand: computeSupplyDemand(advertisedGroup, soldGroup),
  };
}

/**
 * Derives the saved-filters panel metrics from a statistics_v2 response fetched with
 * a metrics request body (all market categories included, offset 0).
 *
 * @param response — statistics_v2 response for the filter being measured.
 * @param priceReport — Current report; provides my price (recommended) and the report
 *   creation date used to split sold cars into the 30-day window.
 */
export function computeMarketMetrics(
  response: GetStatisticsV2ApiResponse,
  priceReport: PriceReportType
): MarketMetrics {
  const cars = response.cars ?? [];
  const totalHits = response.total_hits ?? cars.length;

  const advertisedCars = cars.filter((car) => car.is_available && !car.is_sold);
  const soldCars = cars.filter((car) => car.is_sold);

  const sortedAdvertisedPrices = advertisedCars
    .map((car) => car.price_with_vat)
    .filter(isNotNil)
    .sort((left, right) => left - right);

  const advertisedGroup: MarketMetricsGroup = {
    count: advertisedCars.length,
    averagePrice: averagePrice(advertisedCars),
  };
  const soldGroup: MarketMetricsGroup = {
    count: soldCars.length,
    averagePrice: averagePrice(soldCars),
  };

  return {
    totalHits,
    isTruncated: totalHits > cars.length,
    advertised: {
      ...advertisedGroup,
      minPrice: sortedAdvertisedPrices[0] ?? null,
      maxPrice: sortedAdvertisedPrices[sortedAdvertisedPrices.length - 1] ?? null,
    },
    sold: soldGroup,
    sold30Count: soldCars.filter((car) => isSoldIn30Days(priceReport, car)).length,
    position: computePosition(
      sortedAdvertisedPrices,
      priceReport.sellingPrice.recommended.value ?? null
    ),
    pageEndPrices: computePageEndPrices(sortedAdvertisedPrices),
    priceCurve: downsamplePriceCurve(sortedAdvertisedPrices),
    supplyDemand: computeSupplyDemand(advertisedGroup, soldGroup),
  };
}
